import type { ComponentPublicInstance } from 'vue';
import { computed, defineComponent, inject } from 'vue';
import { addToArrayDistinct, replaceInArray } from '@/helpers/array';
import {
    provideActiveTabIdKey,
    provideRegisterTabKey,
    provideUnregisterTabKey,
    provideUpdateTabKey,
} from '@/components/keys';
import type { Tab } from './types';

/**
 * This mixin provides the common tab functionality.
 * You can watch for attribute changes and execute custom functionality.
 * It handles tab determination & selecting.
 *
 * Specify the attribute `urlTabId` to inejct the initial tab from the url.
 * If none is provided, the first tab will be selected.
 */
export default defineComponent({
    name: 'TabsPanelMixin',

    provide() {
        return {
            [provideRegisterTabKey as symbol]: this.registerTab,
            [provideUnregisterTabKey as symbol]: this.unregisterTab,
            [provideActiveTabIdKey as symbol]: computed(() => this.activeTabId),
            [provideUpdateTabKey as symbol]: this.updateTab,
        };
    },

    props: {
        /**
         * The model value
         */
        modelValue: {
            type: String,
            default: null,
        },
    },

    emits: ['update:model-value'],

    data: () => ({
        tabs: [] as Tab[],
        children: [],
        /* only for internal use, prefer using activeTab & selectTab */
        activeTabId: null as string | null,
    }),

    computed: {
        activeTab() {
            return this.findTab(this.activeTabId) || this.tabs[0] || null;
        },
        urlTabId: () => undefined as undefined | string,
        value() {
            return this.modelValue;
        },
    },

    watch: {
        activeTab(tab) {
            this.scrollTabIntoView(tab.id);
            this.$emit('update:model-value', tab.id);
        },
        modelValue(tabId) {
            if (tabId === this.activeTab.id) return;

            this.selectTab(tabId);
        },
    },

    created() {
        this.activeTabId = this.urlTabId || this.modelValue || null;
    },

    methods: {
        registerTab(tab: Tab) {
            addToArrayDistinct(tab, this.tabs, (el) => el.id === tab.id);
        },
        unregisterTab(id: string) {
            this.tabs = this.tabs.filter((tab) => tab.id !== id);
        },
        findTab(id: string | null) {
            return this.tabs.find((tab) => tab.id === id);
        },
        scrollTabIntoView(id: string) {
            const tabRef = (this.$refs[id] as Array<HTMLElement | ComponentPublicInstance> | undefined)?.[0];
            const tabElement = tabRef && '$el' in tabRef ? tabRef.$el : tabRef;
            const panel = this.$refs.panel as HTMLElement | undefined;

            if (!tabElement || !panel) {
                return;
            }

            const tabRect = tabElement.getBoundingClientRect();
            const elementIsOutToTheLeft = tabRect.left < 0;
            const elementIsOutToTheRight = tabRect.right > window.innerWidth;
            const elementIsOutToTheBottom = tabRect.bottom > window.innerHeight;

            if (elementIsOutToTheLeft) {
                panel.scrollLeft += tabRect.left;
            }

            if (elementIsOutToTheRight) {
                panel.scrollLeft += tabRect.right - window.innerWidth;
            }

            if (elementIsOutToTheBottom) {
                panel.scrollIntoView();
            }
        },
        selectTab(id: string) {
            if (!this.findTab(id)) return;

            this.activeTabId = id;
        },
        updateTab(tab: Tab) {
            replaceInArray(tab, this.tabs, (t) => t.id === tab.id);
        },
    },
});
